import * as THREE from 'three';

/* ═══════════════════════════════════════════════════════════════════════════
   Pool de lumières FIXE — le piège n°4 du plan (§9).

   En forward rendering, le nombre de lumières est un `#define`. Conséquence :
   **ajouter ou retirer une lumière recompile TOUS les programmes de la scène.**
   Une PointLight allumée à chaque explosion produirait donc un freeze de 100 à
   500 ms au moment précis où le jeu doit être le plus spectaculaire — le boss
   qui explose.

   La parade tient en une phrase : les lumières sont allouées au boot et ne
   sont JAMAIS ajoutées ni retirées. On ne pilote que `position`, `color` et
   `intensity`. Mettre `intensity` à zéro neutralise la contribution sans
   toucher au nombre de lumières, donc sans recompilation.

   Trois suffisent. Elles sont attribuées par priorité — les explosions les plus
   grosses et les plus proches d'abord — et si les trois sont prises, la
   quatrième explosion se contente de l'émissif et du bloom. Personne ne le
   remarquera : ce que remplacent réellement les vraies lumières, c'est 2 % du
   coût pour 90 % du ressenti (PLAN.md §4.6).
   ═══════════════════════════════════════════════════════════════════════════ */

const POOL_SIZE = 3;

interface Slot {
  readonly light: THREE.PointLight;
  /** Temps restant, en secondes. */
  life: number;
  /** Durée totale, pour l'extinction. */
  span: number;
  peak: number;
  priority: number;
}

export class LightPool {
  private readonly slots: Slot[] = [];

  constructor(scene: THREE.Scene) {
    for (let i = 0; i < POOL_SIZE; i++) {
      /* ⚠️ `decay = 0`, et ce n'est PAS un oubli du réglage physique.
         En modèle physique (decay = 2, l'atténuation vaut 1/d²), une lampe
         d'intensité 9 posée à 40 unités d'un ennemi à ~50-150 unités ne
         l'éclaire QUE de ~9/50² ≈ 0,004 : rigoureusement invisible. C'est
         pourquoi le joueur ne voyait aucun ennemi réagir aux explosions. Or les
         intensités et portées sont AUTORÉES (9 sur 420 unités pour une lourde,
         14 sur 900 pour un boss, dans `game/explosions.ts`) : de tels chiffres
         ne prennent leur sens qu'avec un modèle « à rayon », pas inverse-carré.
         Avec decay = 0, three ne garde que la fenêtre de coupure
         `[saturate(1 - (d/portée)⁴)]²` : la lampe éclaire à pleine intensité
         près du foyer puis s'éteint en douceur au bord de sa portée. La lueur
         couvre alors réellement les ennemis proches et fond à zéro à la limite —
         exactement l'effet de flash voulu, borné, sans lumière ajoutée. */
      const light = new THREE.PointLight(0xffffff, 0, 0, 0);
      light.intensity = 0;
      // ⚠️ Ajoutées UNE FOIS, ici, et plus jamais retirées.
      scene.add(light);
      this.slots.push({ light, life: 0, span: 1, peak: 0, priority: 0 });
    }
  }

  /**
   * Demande un éclat lumineux. Si les trois lampes sont occupées, remplace la
   * moins prioritaire — et si toutes sont plus prioritaires, ne fait rien.
   * L'échec est silencieux et voulu : mieux vaut une explosion sans lumière
   * qu'un freeze.
   */
  flash(
    x: number,
    y: number,
    z: number,
    color: number,
    intensity: number,
    distance: number,
    durationSec: number,
    priority: number,
  ): boolean {
    let target: Slot | null = null;

    // Une lampe éteinte d'abord ; sinon la moins prioritaire.
    for (const s of this.slots) {
      if (s.life <= 0) {
        target = s;
        break;
      }
    }
    if (!target) {
      let worst = this.slots[0]!;
      for (const s of this.slots) if (s.priority < worst.priority) worst = s;
      if (worst.priority >= priority) return false;
      target = worst;
    }

    target.light.position.set(x, y, z);
    target.light.color.setHex(color);
    target.light.distance = distance;
    target.peak = intensity;
    target.life = durationSec;
    target.span = durationSec;
    target.priority = priority;
    target.light.intensity = intensity;
    return true;
  }

  /** `dt` en secondes. Extinction quadratique : la lumière s'effondre vite,
   *  comme un vrai flash, au lieu de s'attarder. */
  advance(dt: number): void {
    for (const s of this.slots) {
      if (s.life <= 0) continue;
      s.life -= dt;
      if (s.life <= 0) {
        s.life = 0;
        s.priority = 0;
        // intensity = 0 neutralise la contribution SANS retirer la lumière,
        // donc sans recompiler quoi que ce soit.
        s.light.intensity = 0;
        continue;
      }
      const k = s.life / s.span;
      s.light.intensity = s.peak * k * k;
    }
  }

  /** Nombre de lampes actuellement actives — utile en surcouche de debug. */
  get activeCount(): number {
    let n = 0;
    for (const s of this.slots) if (s.life > 0) n++;
    return n;
  }

  get size(): number {
    return POOL_SIZE;
  }
}
